/**
 * Formatting transform values for the CLIPBOARD.
 *
 * A different job from displaying them: whatever is copied gets pasted into a
 * spreadsheet, a code editor, a chat message or another DCC tool, so it has to
 * be plain text, lossless enough to round-trip, and free of the noise floating
 * point leaves behind (`0.30000000000000004`, `-1e-17`, `-0`).
 *
 * Deliberately dependency-free — plain objects and arrays in, strings out — so
 * these serve the tree context menu, the inspector, and whatever needs them next
 * without dragging three.js into the caller.
 */

/** Significant decimals kept. Enough to round-trip a transform without turning
 *  every value into a wall of digits. */
const DECIMALS = 6;
/** Below this, a value is zero for every practical purpose. Rotations routinely
 *  leave -1e-17 behind, and "-0" is confusing to paste. */
const EPSILON = 1e-9;

/** One number, cleaned up for pasting. */
export function formatNumber(value: number): string {
    // Non-finite values have no useful text form and would poison a paste; a
    // transform component is never legitimately NaN/Infinity.
    if (!Number.isFinite(value)) return "0";
    if (Math.abs(value) < EPSILON) return "0";
    // toFixed, not toPrecision: toPrecision switches to exponent notation for
    // small values, and "1e-7" is not a number to a spreadsheet or a Unity field.
    // The trailing-zero trim keeps whole numbers whole.
    return value.toFixed(DECIMALS).replace(/\.?0+$/, "");
}

export interface Vector3Like { x: number; y: number; z: number }
export interface QuaternionLike { x: number; y: number; z: number; w: number }

/** `x, y, z` — plain enough to paste anywhere. */
export function formatVector3(v: Vector3Like): string {
    return `${formatNumber(v.x)}, ${formatNumber(v.y)}, ${formatNumber(v.z)}`;
}

/**
 * `x, y, z, w` — in THAT order.
 *
 * Order is the thing to get wrong: three stores xyzw, plenty of maths libraries
 * write w first, and a mis-ordered paste produces a different rotation rather
 * than an error anyone would notice.
 */
export function formatQuaternion(q: QuaternionLike): string {
    return `${formatNumber(q.x)}, ${formatNumber(q.y)}, ${formatNumber(q.z)}, ${formatNumber(q.w)}`;
}

const RAD2DEG = 180 / Math.PI;

/**
 * Euler angles in DEGREES, from three's radians.
 *
 * Every inspector a user might paste into shows degrees, so copying raw radians
 * would look plausible and be wrong by a factor of 57.
 */
export function formatEulerDegrees(e: Vector3Like): string {
    return formatVector3({ x: e.x * RAD2DEG, y: e.y * RAD2DEG, z: e.z * RAD2DEG });
}

/**
 * A 4x4 matrix as four lines, reading the way the matrix looks on paper.
 *
 * Takes three's COLUMN-major `.elements` layout and transposes for display —
 * pasting the raw element order would silently transpose the matrix for the
 * reader, which is the sort of thing nobody catches until geometry is inside out.
 */
export function formatMatrix4(elements: readonly number[]): string {
    if (elements.length !== 16) {
        throw new Error(`formatMatrix4 expects 16 elements, got ${elements.length}`);
    }
    const rows: string[] = [];
    for (let row = 0; row < 4; row++) {
        const cells: string[] = [];
        for (let column = 0; column < 4; column++) {
            cells.push(formatNumber(elements[column * 4 + row]!));
        }
        rows.push(cells.join(", "));
    }
    return rows.join("\n");
}

/** Negation that never yields -0. These functions return VALUES rather than
 *  strings, so the wart escapes into whatever consumes them: -0 compares unequal
 *  to 0 and prints as "-0". formatNumber cleans it up for display, but only for
 *  callers that format immediately. */
function negate(n: number): number {
    return n === 0 ? 0 : -n;
}

/**
 * Position from glTF/three space into Unity's.
 *
 * three loads glTF natively (both right-handed, Y-up), so three's values ARE
 * glTF values and this is the glTF->Unity conversion. Taken from UnityGLTF's
 * `SchemaExtensions.CoordinateSpaceConversionScale` — the importer that actually
 * brings Needle scenes into Unity — rather than derived from first principles:
 *
 *     CoordinateSpaceConversionScale = (-1, 1, 1)
 *
 * so X is negated. Z is the intuitive guess and it is WRONG; a mirrored object
 * looks perfectly plausible until you notice the text reads backwards.
 *
 * Its own inverse, so the same function converts in both directions.
 */
export function toUnityPosition(v: Vector3Like): Vector3Like {
    return { x: negate(v.x), y: v.y, z: v.z };
}

/**
 * Rotation from glTF/three space into Unity's.
 *
 * UnityGLTF scales the rotation AXIS by the conversion scale and then negates it
 * wholesale for the handedness flip — `(x,y,z)` -> `(-x,y,z)` -> `(x,-y,-z)` —
 * leaving `w` untouched. Also its own inverse.
 */
export function toUnityQuaternion(q: QuaternionLike): QuaternionLike {
    return { x: q.x, y: negate(q.y), z: negate(q.z), w: q.w };
}

export interface TransformValues {
    position: Vector3Like;
    /** Euler angles in RADIANS (three's convention); printed as degrees. */
    rotationEuler: Vector3Like;
    quaternion: QuaternionLike;
    scale: Vector3Like;
}

/**
 * A whole transform as labelled lines.
 *
 * Labelled because this lands in a chat message, a note or an issue, where three
 * unlabelled number triples are indistinguishable from one another. The
 * quaternion is included alongside the euler angles rather than instead of them:
 * euler is what people read, but it cannot always express the same rotation
 * unambiguously, so throwing it away loses information.
 */
export function formatTransformBlock(t: TransformValues): string {
    return [
        `Position: ${formatVector3(t.position)}`,
        `Rotation: ${formatEulerDegrees(t.rotationEuler)}`,
        `Quaternion: ${formatQuaternion(t.quaternion)}`,
        `Scale: ${formatVector3(t.scale)}`,
    ].join("\n");
}

/**
 * Unity's clipboard prefix for a copied Transform component. Unity's OWN string,
 * captured from a real copy — a wrong wrapper is silently ignored on paste,
 * which reads as our bug and cannot be diagnosed from the Unity side.
 */
const UNITY_TRANSFORM_PREFIX = "UnityEditor.TransformWorldPlacementJSON:";

export interface UnityPlacement {
    position: Vector3Like;
    rotation: QuaternionLike;
    scale: Vector3Like;
}

/**
 * A Transform for Unity's "Paste Component Values", in Unity's own format.
 *
 * Note **WorldPlacement**: Unity's transform copy carries WORLD values, so this
 * must be fed world-space input — local values would paste the object to the
 * wrong place for anything with a parent.
 *
 * Values are converted into Unity's left-handed space on the way out (see
 * {@link toUnityPosition} / {@link toUnityQuaternion}). Scale is NOT flipped: it
 * is a magnitude per axis and UnityGLTF leaves it alone.
 *
 * Full precision, deliberately — this payload is machine-read, so the rounding
 * that makes {@link formatNumber} pleasant to paste by hand would only lose
 * information here.
 */
export function formatUnityTransformClipboard(placement: UnityPlacement): string {
    const position = toUnityPosition(placement.position);
    const rotation = toUnityQuaternion(placement.rotation);
    const scale = placement.scale;
    // Hand-built rather than JSON.stringify: C# writes floats WITH a decimal
    // point (`0.0`, `1.0`) and JSON.stringify writes `0`. Unity's clipboard
    // parser is its own, not a lenient JSON reader — it refused the paste
    // outright (every entry under Paste greyed out) until this matched. Key
    // ORDER is Unity's too, so a copied string stays diffable against one Unity
    // produced.
    const v3 = (v: Vector3Like) => `{"x":${csFloat(v.x)},"y":${csFloat(v.y)},"z":${csFloat(v.z)}}`;
    const q = (n: QuaternionLike) =>
        `{"x":${csFloat(n.x)},"y":${csFloat(n.y)},"z":${csFloat(n.z)},"w":${csFloat(n.w)}}`;
    return `${UNITY_TRANSFORM_PREFIX}{"position":${v3(position)},"rotation":${q(rotation)},"scale":${v3(scale)}}`;
}

/**
 * A number the way C# writes a float: always with a decimal point, otherwise
 * shortest round-trip (which is what C#'s "R" produces and what JS gives by
 * default). Full precision — this payload is machine-read, so the rounding that
 * makes {@link formatNumber} pleasant to paste by hand would only lose data.
 */
function csFloat(value: number): string {
    if (!Number.isFinite(value)) return "0.0";
    const text = String(value);
    return text.includes(".") || text.includes("e") || text.includes("E") ? text : `${text}.0`;
}

/**
 * A Vector3 for Unity's per-field paste (right-click a Vector3 field -> Paste),
 * converted into Unity space.
 *
 * Matches Unity's own emission: no spaces, and 7 decimals — which is float
 * precision, so more digits would be noise.
 */
export function formatUnityVector3(v: Vector3Like): string {
    const u = toUnityPosition(v);
    const n = (value: number) => String(Number(value.toFixed(7)));
    return `Vector3(${n(u.x)},${n(u.y)},${n(u.z)})`;
}
